import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";

const Field = ({ label, hint, children, testid }) => (
  <label data-testid={testid} style={{ display: "block", marginBottom: 14 }}>
    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>{label}</div>
    {hint && <div style={{ fontSize: 11, color: "var(--ink-mute)", marginBottom: 4 }}>{hint}</div>}
    {children}
  </label>
);

const Input = (props) => (
  <input
    {...props}
    style={{
      width: "100%", padding: "8px 10px",
      border: "1px solid var(--ink-line)", borderRadius: 6,
      fontSize: 14, background: "white", ...(props.style || {}),
    }}
  />
);

const Section = ({ title, blurb, children, testid }) => (
  <div data-testid={testid} style={{
    border: "1px solid var(--ink-line)", borderRadius: 8, padding: 18, marginBottom: 16,
  }}>
    <div style={{ fontWeight: 700, marginBottom: 4, color: "var(--ink)" }}>{title}</div>
    {blurb && <div style={{ fontSize: 12, color: "var(--ink-mute)", marginBottom: 14 }}>{blurb}</div>}
    {children}
  </div>
);

// Display-friendly labels for the raw match-signal identifiers stored on
// `recognition_signal_weights.match.*`. Anything not in this map falls back
// to a sentence-cased version of the underscore-separated identifier.
const SIGNAL_LABELS = {
  amount_exact:           "Amount · exact match",
  amount_within_tolerance: "Amount · within tolerance",
  date_within_tolerance:   "Date · within tolerance",
  reference_match:         "Reference matches",
  description_keyword:     "Description keyword overlap",
  dr_cr_match:             "DR/CR direction match",
};
function signalLabel(name) {
  if (SIGNAL_LABELS[name]) return SIGNAL_LABELS[name];
  const spaced = name.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export default function EngineConfiguration() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => { (async () => {
    setLoading(true);
    try { setSettings(await api.getSettings()); }
    finally { setLoading(false); }
  })(); }, []);

  const patchScore = (path, value) => {
    setSettings(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) {
        cur[keys[i]] = cur[keys[i]] ?? {};
        cur = cur[keys[i]];
      }
      cur[keys.at(-1)] = value;
      return next;
    });
  };

  const onSave = async () => {
    try {
      setSaving(true); setMsg(null);
      const saved = await api.saveSettings({
        date_match_tolerance_days: Number(settings.date_match_tolerance_days),
        auto_match_enabled: settings.auto_match_enabled,
        allow_variance_completion: settings.allow_variance_completion,
        require_completion_notes: settings.require_completion_notes,
        recognition_thresholds: {
          high: Number(settings.recognition_thresholds.high),
          medium: Number(settings.recognition_thresholds.medium),
          low: Number(settings.recognition_thresholds.low),
        },
        recognition_signal_weights: settings.recognition_signal_weights,
        duplicate_score_thresholds: {
          high: Number(settings.duplicate_score_thresholds.high),
          medium: Number(settings.duplicate_score_thresholds.medium),
          low: Number(settings.duplicate_score_thresholds.low),
        },
      });
      setSettings(saved);
      setMsg({ type: "ok", text: "Engine configuration saved. New runs will use the updated values." });
    } catch (e) {
      setMsg({ type: "err", text: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  if (loading || !settings) return <div className="empty">Loading engine configuration…</div>;

  const sigWeights = settings.recognition_signal_weights?.match || {};

  return (
    <div data-testid="page-engine-configuration" style={{ maxWidth: 900 }}>
      <div className="heading h1">Engine Configuration</div>
      <div className="muted" style={{ marginTop: 4, marginBottom: 18, fontSize: 14 }}>
        Tunable parameters for the Recognition Engine and Duplicate Detection.
        Changes apply to the next run; existing recommendations and clusters are immutable.
      </div>

      <Section title="Completion policy" testid="section-completion-policy"
               blurb="Controls whether periods can be completed with open variance and whether notes are mandatory.">
        <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }} data-testid="field-allow-variance">
          <input type="checkbox"
                 checked={!!settings.allow_variance_completion}
                 onChange={e => patchScore("allow_variance_completion", e.target.checked)}
                 data-testid="input-allow-variance-completion" />
          Allow completion with unexplained variance
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 8 }} data-testid="field-require-notes">
          <input type="checkbox"
                 checked={!!settings.require_completion_notes}
                 onChange={e => patchScore("require_completion_notes", e.target.checked)}
                 data-testid="input-require-completion-notes" />
          Require completion notes
        </label>
      </Section>

      <Section title="Matching defaults" testid="section-matching-defaults"
               blurb="Defaults applied to every reconciliation match cycle.">
        <Field label="Date tolerance (days)"
               hint="A bank/GL match earns a date-tolerance signal when the two dates fall within this many days of each other. Higher tolerance helps businesses whose GL is dated at month-end while bank transactions fall throughout the month."
               testid="field-date-tolerance">
          <Input type="number" min="0" max="31"
                 value={settings.date_match_tolerance_days}
                 onChange={e => patchScore("date_match_tolerance_days", e.target.value)}
                 data-testid="input-date-tolerance-days" />
        </Field>
        <label style={{ display: "flex", alignItems: "center", gap: 8 }} data-testid="field-auto-match-enabled">
          <input type="checkbox" checked={!!settings.auto_match_enabled}
                 onChange={e => patchScore("auto_match_enabled", e.target.checked)}
                 data-testid="input-auto-match-enabled" />
          Auto-match enabled at import (legacy compatibility)
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}
               data-testid="field-auto-accept-high">
          <input type="checkbox"
                 checked={!!settings.auto_accept_high_band_on_import}
                 onChange={e => patchScore("auto_accept_high_band_on_import", e.target.checked)}
                 data-testid="input-auto-accept-high" />
          Auto-accept HIGH-band recommendations immediately after import
        </label>
        <div className="muted" style={{ fontSize: 11, marginLeft: 22, marginTop: 2 }}>
          When on (default), every successful import triggers a Recognition run
          and every HIGH-band (≥ {settings?.recognition_thresholds?.high ?? "0.90"})
          match candidate is accepted automatically — no Controller click required.
          Each acceptance still goes through the governed create_match_group path
          and writes an audit row.  Turn off if you want to inspect every match
          before it becomes a MatchGroup.
        </div>
      </Section>

      <Section title="Recognition Engine — confidence bands" testid="section-recognition-bands"
               blurb="High and Medium recommendations may be accepted directly; Low requires an explicit controller override.">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
          <Field label="High threshold (≥)" testid="field-high">
            <Input type="number" min="0" max="1" step="0.01"
                   value={settings.recognition_thresholds.high}
                   onChange={e => patchScore("recognition_thresholds.high", e.target.value)}
                   data-testid="input-threshold-high" />
          </Field>
          <Field label="Medium threshold (≥)" testid="field-medium">
            <Input type="number" min="0" max="1" step="0.01"
                   value={settings.recognition_thresholds.medium}
                   onChange={e => patchScore("recognition_thresholds.medium", e.target.value)}
                   data-testid="input-threshold-medium" />
          </Field>
          <Field label="Low threshold (≥)" testid="field-low">
            <Input type="number" min="0" max="1" step="0.01"
                   value={settings.recognition_thresholds.low}
                   onChange={e => patchScore("recognition_thresholds.low", e.target.value)}
                   data-testid="input-threshold-low" />
          </Field>
        </div>
      </Section>

      <Section title="Recognition Engine — signal weights" testid="section-recognition-weights"
               blurb="Each signal contributes its weight × proportion-met to the score. Sum may exceed 1.0; final score is clamped to [0, 1].">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12 }}>
          {Object.entries(sigWeights).map(([name, weight]) => (
            <Field key={name} label={signalLabel(name)} testid={`field-weight-${name}`}>
              <Input type="number" min="0" max="1" step="0.01" value={weight}
                     onChange={e => patchScore(`recognition_signal_weights.match.${name}`, Number(e.target.value))}
                     data-testid={`input-weight-${name}`} />
            </Field>
          ))}
        </div>
      </Section>

      <Section title="Duplicate Detection — score thresholds" testid="section-duplicate-thresholds"
               blurb="Used to band clustered candidate duplicates.">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
          {["high", "medium", "low"].map(k => (
            <Field key={k} label={`${k.toUpperCase()} threshold (≥)`} testid={`field-dup-${k}`}>
              <Input type="number" min="0" max="1" step="0.01"
                     value={settings.duplicate_score_thresholds[k]}
                     onChange={e => patchScore(`duplicate_score_thresholds.${k}`, e.target.value)}
                     data-testid={`input-dup-threshold-${k}`} />
            </Field>
          ))}
        </div>
      </Section>

      <div style={{
        position: "sticky", bottom: 0, background: "white",
        padding: "12px 0", marginTop: 8, display: "flex", gap: 12, alignItems: "center",
        borderTop: "1px solid var(--ink-line)",
      }}>
        <button className="btn primary" onClick={onSave} disabled={saving} data-testid="save-engine-btn">
          {saving ? "Saving…" : "Save Configuration"}
        </button>
        {msg && (
          <div style={{ fontSize: 13, color: msg.type === "ok" ? "#1f5e3a" : "#a30000" }}
               data-testid="engine-msg">{msg.text}</div>
        )}
      </div>
    </div>
  );
}
